import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { api } from '@/lib/api';
import type * as API from '@/types/api';
import { useAccountStore } from '@nimbitlabs/features';

interface MonitoringConfigsState {
  configs: API.ServiceMonitoringConfigurationDto[];
  results: Record<string, API.MonitoringResultDto[]>;
  summaries: Record<string, any>;
  recentFailures: API.ServiceMonitoringConfigurationDto[];
  latestStatuses: API.ServiceMonitoringConfigurationDto[];
  loading: boolean;
  error: string | null;
  
  fetchConfigs: () => Promise<void>;
  fetchResults: (id: string, limit?: number) => Promise<void>;
  fetchSummary: (id: string, days?: number) => Promise<void>;
  fetchRecentFailures: (hours?: number) => Promise<void>;
  fetchLatestStatuses: () => Promise<void>;
  getConfigsByServiceId: (serviceId: string) => API.ServiceMonitoringConfigurationDto[];
  getConfigsByServiceEnvironment: (serviceEnvironmentId: string) => API.ServiceMonitoringConfigurationDto[];
  createConfig: (data: API.CreateServiceMonitoringConfigurationDto) => Promise<API.ServiceMonitoringConfigurationDto>;
  updateConfig: (id: string, data: API.UpdateServiceMonitoringConfigurationDto) => Promise<void>;
  enableConfig: (id: string) => Promise<void>;
  disableConfig: (id: string) => Promise<void>;
  deleteConfig: (id: string) => Promise<void>;
  clearError: () => void;
}

const monitoringConfigsStore = create<MonitoringConfigsState>()(
  subscribeWithSelector((set, get) => ({
  configs: [],
  results: {},
  summaries: {},
  recentFailures: [],
  latestStatuses: [],
  loading: false,
  error: null,
  
  fetchConfigs: async () => {
    set({ loading: true, error: null });
    try {
      const data = await api.serviceMonitoringConfigurations.getAll();
      set({ configs: data, loading: false });
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to fetch monitoring configs', loading: false });
    }
  },
  
  fetchResults: async (id: string, limit: number = 100) => {
    try {
      const data = await api.serviceMonitoringConfigurations.getResults(id, limit);
      set(state => ({
        results: { ...state.results, [id]: data },
      }));
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to fetch monitoring results' });
    }
  },
  
  fetchSummary: async (id: string, days: number = 7) => {
    try {
      const data = await api.serviceMonitoringConfigurations.getSummary(id, days);
      set(state => ({
        summaries: { ...state.summaries, [id]: data },
      }));
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to fetch monitoring summary' });
    }
  },
  
  fetchRecentFailures: async (hours: number = 24) => {
    try {
      const data = await api.serviceMonitoringConfigurations.getRecentFailures(hours);
      set({ recentFailures: data });
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to fetch recent failures' });
    }
  },
  
  fetchLatestStatuses: async () => {
    try {
      const data = await api.serviceMonitoringConfigurations.getLatestStatuses();
      set({ latestStatuses: data });
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to fetch latest statuses' });
    }
  },
  
  getConfigsByServiceId: (serviceId: string) => {
    return get().configs.filter(config => config.serviceId === serviceId);
  },
  
  getConfigsByServiceEnvironment: (serviceEnvironmentId: string) => {
    return get().configs.filter(config => config.serviceEnvironmentId === serviceEnvironmentId);
  },
  
  createConfig: async (data: API.CreateServiceMonitoringConfigurationDto) => {
    try {
      const newConfig = await api.serviceMonitoringConfigurations.create(data);
      set(state => ({
        configs: [...state.configs, newConfig],
      }));
      return newConfig;
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to create monitoring config' });
      throw e;
    }
  },
  
  updateConfig: async (id: string, data: API.UpdateServiceMonitoringConfigurationDto) => {
    const original = get().configs.find(c => c.id === id);
    
    // Optimistically update local state
    set(state => ({
      configs: state.configs.map(c =>
        c.id === id ? { ...c, ...data, updatedAt: new Date().toISOString() } : c
      )
    }));
    
    try {
      const updated = await api.serviceMonitoringConfigurations.update(id, data);
      set(state => ({
        configs: state.configs.map(c => c.id === id ? updated : c)
      }));
    } catch (e: any) {
      // Revert on error
      if (original) {
        set(state => ({
          configs: state.configs.map(c => c.id === id ? original : c)
        }));
      }
      set({ error: e?.message ?? 'Failed to update monitoring config' });
      throw e;
    }
  },
  
  enableConfig: async (id: string) => {
    const original = get().configs.find(c => c.id === id);
    
    // Optimistically update local state
    set(state => ({
      configs: state.configs.map(c =>
        c.id === id ? { ...c, isEnabled: true, updatedAt: new Date().toISOString() } : c
      )
    }));
    
    try {
      const updated = await api.serviceMonitoringConfigurations.enable(id);
      set(state => ({
        configs: state.configs.map(c => c.id === id ? updated : c)
      }));
    } catch (e: any) {
      // Revert on error
      if (original) {
        set(state => ({
          configs: state.configs.map(c => c.id === id ? original : c)
        }));
      }
      set({ error: e?.message ?? 'Failed to enable monitoring config' });
      throw e;
    }
  },
  
  disableConfig: async (id: string) => {
    const original = get().configs.find(c => c.id === id);
    
    // Optimistically update local state
    set(state => ({
      configs: state.configs.map(c =>
        c.id === id ? { ...c, isEnabled: false, updatedAt: new Date().toISOString() } : c
      )
    }));
    
    try {
      const updated = await api.serviceMonitoringConfigurations.disable(id);
      set(state => ({
        configs: state.configs.map(c => c.id === id ? updated : c)
      }));
    } catch (e: any) {
      // Revert on error
      if (original) {
        set(state => ({
          configs: state.configs.map(c => c.id === id ? original : c)
        }));
      }
      set({ error: e?.message ?? 'Failed to disable monitoring config' });
      throw e;
    }
  },
  
  deleteConfig: async (id: string) => {
    try {
      await api.serviceMonitoringConfigurations.delete(id);
      set(state => ({
        configs: state.configs.filter(c => c.id !== id),
      }));
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to delete monitoring config' });
      throw e;
    }
  },
  
  clearError: () => set({ error: null }),
  }))
);

export const useMonitoringConfigsStore = monitoringConfigsStore;

// Subscribe to account changes and auto-fetch
useAccountStore.subscribe(
  (state) => state.currentAccount,
  (currentAccount) => {
    if (currentAccount?.id) {
      monitoringConfigsStore.getState().fetchConfigs();
    } else {
      monitoringConfigsStore.setState({ configs: [] });
    }
  }
);
